'use client'

import Image from 'next/image'
import { useCallback, useEffect, useMemo, useState } from 'react'
import useEmblaCarousel from 'embla-carousel-react'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import type { Slide } from 'yet-another-react-lightbox'
import BlurredLightbox from '@/components/ui/BlurredLightbox'
import LightboxImageSlide from '@/components/ui/LightboxImageSlide'
import { cn } from '@/lib/utils'
import { getFocalPointStyle } from '@/utilities/getFocalPointStyle'
import { getMediaUrl } from '@/utilities/getMediaUrl'
import { Media } from '@/components/Media'
import Button from '@/components/ui/button'
import { getOptimizedImageProps } from '@inoo-ch/payload-image-optimizer/frontend'
import type { Media as MediaType } from '@/payload-types'

type LightboxSlideWithBlur = Slide & { blurDataURL?: string; resource?: MediaType | null }

export interface GalleryImageItem {
  resource?: MediaType | null // Payload media object (preferred)
  src?: string // Fallback for processed data
  alt: string
  focalX?: number | null // Keep for backward compatibility
  focalY?: number | null
}

export interface HoverGrowGalleryProps {
  images: GalleryImageItem[]
  className?: string
  containerClassName?: string
  /** Tailwind height class for desktop/tablet row (e.g., h-80, h-96). Default: h-96 */
  heightClass?: string
  /** Tailwind height class for mobile grid container (e.g., h-56, h-64). Default: h-64 */
  mobileHeightClass?: string
  /** Gap between items. Default: gap-4 */
  gapClass?: string
  /** Auto-rotate through images when not hovering. Default: true */
  autoRotate?: boolean
  /** Interval for auto-rotation in milliseconds. Default: 3500 */
  rotateIntervalMs?: number
  /** Sizes attribute forwarded to next/image */
  sizes?: string
  /** Number of images per desktop slide. Default: 4 */
  chunkSize?: number
}

export default function HoverGrowGallery({
  images,
  className = '',
  containerClassName = '',
  heightClass = 'h-96',
  mobileHeightClass = 'h-64',
  gapClass = 'gap-4',
  autoRotate = true,
  rotateIntervalMs = 3500,
  sizes = '(max-width: 768px) 100vw, 50vw',
  chunkSize = 4,
}: HoverGrowGalleryProps) {
  const CHUNK_SIZE = chunkSize
  const totalImages = images.length
  const shouldUseDesktopCarousel = totalImages > CHUNK_SIZE
  const shouldUseMobileCarousel = totalImages > 1

  const [activeIndex, setActiveIndex] = useState<number>(0)
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null)
  const [canLeft, setCanLeft] = useState(false)
  const [canRight, setCanRight] = useState(false)
  const [canLeftMobile, setCanLeftMobile] = useState(false)
  const [canRightMobile, setCanRightMobile] = useState(false)
  const [lightboxOpen, setLightboxOpen] = useState(false)
  const [lightboxIndex, setLightboxIndex] = useState(0)

  const [emblaRef, embla] = useEmblaCarousel(
    shouldUseDesktopCarousel
      ? {
          loop: true,
          align: 'start',
          skipSnaps: false,
        }
      : undefined,
  )

  const [emblaMobileRef, emblaMobile] = useEmblaCarousel(
    shouldUseMobileCarousel
      ? {
          loop: true,
          align: 'start',
          skipSnaps: false,
        }
      : undefined,
  )

  const slides = useMemo(() => {
    if (totalImages === 0) return []
    if (!shouldUseDesktopCarousel) return [images]
    const chunks: GalleryImageItem[][] = []
    for (let i = 0; i < totalImages; i += CHUNK_SIZE) {
      chunks.push(images.slice(i, i + CHUNK_SIZE))
    }
    return chunks
  }, [images, shouldUseDesktopCarousel, totalImages, CHUNK_SIZE])

  const currentIndex = hoveredIndex ?? activeIndex

  // Convert gallery images to lightbox slides
  const lightboxSlides = useMemo<LightboxSlideWithBlur[]>(() => {
    const getBlur = (resource?: MediaType | null) => {
      if (!resource) return undefined
      const optimized = getOptimizedImageProps(resource as any)
      if (optimized.blurDataURL) return optimized.blurDataURL
      if ('thumbhash' in resource && resource.thumbhash) return resource.thumbhash as string
      return undefined
    }

    return images.map((image) => {
      let src = image.src || ''
      let width: number | undefined
      let height: number | undefined

      if (image.resource) {
        src = getMediaUrl(image.resource.url, image.resource.updatedAt)
        width = image.resource.width ?? undefined
        height = image.resource.height ?? undefined
      }

      return {
        src,
        alt: image.alt,
        width,
        height,
        blurDataURL: getBlur(image.resource),
        resource: image.resource ?? null,
      }
    })
  }, [images])

  // Handle lightbox open
  const handleLightboxOpen = useCallback((index: number) => {
    setLightboxIndex(index)
    setLightboxOpen(true)
  }, [])

  useEffect(() => {
    if (!autoRotate) return
    if (hoveredIndex !== null) return // pause rotation while hovering
    if (totalImages <= 1) return

    const id = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % totalImages)
    }, rotateIntervalMs)
    return () => clearInterval(id)
  }, [autoRotate, hoveredIndex, rotateIntervalMs, totalImages])

  useEffect(() => {
    if (!shouldUseDesktopCarousel || !embla) return

    const handleSelect = () => {
      const selected = embla.selectedScrollSnap()
      setCanLeft(embla.canScrollPrev())
      setCanRight(embla.canScrollNext())

      const slideStart = selected * CHUNK_SIZE
      setActiveIndex((prev) => {
        if (prev >= slideStart && prev < slideStart + CHUNK_SIZE && prev < totalImages) {
          return prev
        }
        return Math.min(slideStart, totalImages - 1)
      })
    }

    embla.on('select', handleSelect)
    embla.on('reInit', handleSelect)
    handleSelect()

    return () => {
      embla.off('select', handleSelect)
      embla.off('reInit', handleSelect)
    }
  }, [embla, shouldUseDesktopCarousel, totalImages, CHUNK_SIZE])

  useEffect(() => {
    if (!shouldUseDesktopCarousel || !embla) return
    const targetSlide = Math.floor(activeIndex / CHUNK_SIZE)
    if (targetSlide !== embla.selectedScrollSnap()) {
      embla.scrollTo(targetSlide)
    }
  }, [activeIndex, embla, shouldUseDesktopCarousel, CHUNK_SIZE])

  useEffect(() => {
    if (!shouldUseMobileCarousel || !emblaMobile) return

    const handleSelect = () => {
      setCanLeftMobile(emblaMobile.canScrollPrev())
      setCanRightMobile(emblaMobile.canScrollNext())
    }

    emblaMobile.on('select', handleSelect)
    emblaMobile.on('reInit', handleSelect)
    handleSelect()

    return () => {
      emblaMobile.off('select', handleSelect)
      emblaMobile.off('reInit', handleSelect)
    }
  }, [emblaMobile, shouldUseMobileCarousel])

  useEffect(() => {
    if (activeIndex >= totalImages) {
      setActiveIndex(totalImages > 0 ? totalImages - 1 : 0)
    }
  }, [activeIndex, totalImages])

  const renderDesktopRow = useCallback(
    (subset: GalleryImageItem[], offset: number) => (
      <div
        className={cn('hidden md:flex', gapClass, heightClass, containerClassName)}
        onMouseLeave={() => setHoveredIndex(null)}
      >
        {subset.map((image, index) => {
          const absoluteIndex = offset + index
          const imageKey = image.resource?.id ?? image.src ?? `img-${absoluteIndex}`
          const key = `desktop-${absoluteIndex}-${imageKey}`
          const isActive = absoluteIndex === currentIndex
          const aspectRatio =
            (image.resource?.width ?? 1) / (image.resource?.height ?? 1)
          const growUnits = isActive
            ? 1 + Math.min(aspectRatio, 1.6) * 0.375
            : 1

          return (
            <div
              key={key}
              className="relative overflow-hidden h-full transition-all duration-500 ease-in-out min-w-0"
              style={{ flexGrow: growUnits, flexBasis: 0 }}
            >
              <div
                className="relative h-full w-full cursor-pointer"
                onMouseEnter={() => setHoveredIndex(absoluteIndex)}
                onClick={() => handleLightboxOpen(absoluteIndex)}
              >
                {image.resource ? (
                  <Media
                    resource={image.resource}
                    fill
                    pictureClassName="absolute inset-0 w-full h-full"
                    imgClassName="object-cover"
                    size={sizes}
                    alt={image.alt}
                  />
                ) : (
                  <Image
                    src={image.src || ''}
                    alt={image.alt}
                    fill
                    className="object-cover"
                    sizes={sizes}
                    style={getFocalPointStyle(image.focalX, image.focalY)}
                  />
                )}
              </div>
            </div>
          )
        })}
      </div>
    ),
    [containerClassName, currentIndex, gapClass, heightClass, sizes, handleLightboxOpen],
  )

  const renderMobileItem = useCallback(
    (image: GalleryImageItem, absoluteIndex: number) => (
      <div
        className={cn('relative w-full cursor-pointer', mobileHeightClass, containerClassName)}
        onClick={() => handleLightboxOpen(absoluteIndex)}
      >
        {image.resource ? (
          <Media
            resource={image.resource}
            fill
            pictureClassName="absolute inset-0 w-full h-full"
            imgClassName="object-cover"
            size="100vw"
            alt={image.alt}
          />
        ) : (
          <Image
            src={image.src || ''}
            alt={image.alt}
            fill
            className="object-cover"
            sizes="100vw"
            style={getFocalPointStyle(image.focalX, image.focalY)}
          />
        )}
      </div>
    ),
    [containerClassName, mobileHeightClass, handleLightboxOpen],
  )

  const handlePrev = useCallback(() => {
    if (embla && shouldUseDesktopCarousel) embla.scrollPrev()
    if (emblaMobile && shouldUseMobileCarousel) emblaMobile.scrollPrev()
  }, [embla, emblaMobile, shouldUseDesktopCarousel, shouldUseMobileCarousel])

  const handleNext = useCallback(() => {
    if (embla && shouldUseDesktopCarousel) embla.scrollNext()
    if (emblaMobile && shouldUseMobileCarousel) emblaMobile.scrollNext()
  }, [embla, emblaMobile, shouldUseDesktopCarousel, shouldUseMobileCarousel])

  if (!images || totalImages === 0) return null

  return (
    <div className={cn('w-full', className)}>
      <div className="space-y-6">
        <div className="relative">
          {/* Desktop carousel: chunks of CHUNK_SIZE, flex row */}
          <div className={cn('hidden md:block', shouldUseDesktopCarousel && 'overflow-hidden')} ref={shouldUseDesktopCarousel ? emblaRef : undefined}>
            {shouldUseDesktopCarousel ? (
              <div className="flex">
                {slides.map((slideImages, slideIndex) => {
                  const offset = slideIndex * CHUNK_SIZE
                  return (
                    <div key={`slide-${slideIndex}`} className="flex-none w-full">
                      {renderDesktopRow(slideImages, offset)}
                    </div>
                  )
                })}
              </div>
            ) : (
              renderDesktopRow(images, 0)
            )}
          </div>

          {/* Mobile carousel: one image per slide */}
          <div className={cn('md:hidden', shouldUseMobileCarousel && 'overflow-hidden')} ref={shouldUseMobileCarousel ? emblaMobileRef : undefined}>
            {shouldUseMobileCarousel ? (
              <div className="flex">
                {images.map((image, index) => {
                  const imageKey = image.resource?.id ?? image.src ?? `img-${index}`
                  return (
                    <div key={`mobile-slide-${index}-${imageKey}`} className="flex-none w-full">
                      {renderMobileItem(image, index)}
                    </div>
                  )
                })}
              </div>
            ) : (
              renderMobileItem(images[0]!, 0)
            )}
          </div>

          {shouldUseDesktopCarousel && (
            <div className="mt-6 hidden md:flex items-center justify-center gap-4">
              <Button
                text="ZURÜCK"
                icon={<ChevronLeft className="w-5 h-5" />}
                iconPosition="left"
                variant="primary"
                color="brand"
                onClick={handlePrev}
                disabled={!canLeft}
              />
              <Button
                text="WEITER"
                icon={<ChevronRight className="w-5 h-5" />}
                iconPosition="right"
                variant="primary"
                color="brand"
                onClick={handleNext}
                disabled={!canRight}
              />
            </div>
          )}

          {shouldUseMobileCarousel && (
            <div className="mt-6 flex md:hidden items-center justify-center gap-4">
              <Button
                text="ZURÜCK"
                icon={<ChevronLeft className="w-5 h-5" />}
                iconPosition="left"
                variant="primary"
                color="brand"
                onClick={handlePrev}
                disabled={!canLeftMobile}
              />
              <Button
                text="WEITER"
                icon={<ChevronRight className="w-5 h-5" />}
                iconPosition="right"
                variant="primary"
                color="brand"
                onClick={handleNext}
                disabled={!canRightMobile}
              />
            </div>
          )}
        </div>
      </div>

      {/* Lightbox Gallery */}
      <BlurredLightbox
        open={lightboxOpen}
        close={() => setLightboxOpen(false)}
        index={lightboxIndex}
        slides={lightboxSlides as Slide[]}
        on={{
          view: ({ index }) => {
            setLightboxIndex(index)
          },
        }}
        render={{
          slide: ({ slide, rect }) => {
            const s = slide as LightboxSlideWithBlur
            return (
              <LightboxImageSlide
                src={s.src}
                alt={s.alt ?? ''}
                resource={s.resource}
                intrinsicWidth={s.width}
                intrinsicHeight={s.height}
                rect={rect}
                blurDataURL={s.blurDataURL}
              />
            )
          },
        }}
      />
    </div>
  )
}
